import { logger } from '../../utils/logger';

export type ParentEventId = number | string;

export interface ParentEventDetails {
  id: ParentEventId;
  numberAttending: number;
  totalCapacity: number | null;
  isAttending: boolean;
}

interface ParentMessageRequest<TPayload> {
  requestType: 'EVENT_DETAILS' | 'UPDATE_RSVP';
  requestId: string;
  payload: TPayload;
}

type ParentMessageResponse<TData> =
  | {
      requestType?: string;
      type?: string;
      requestId: string;
      success: true;
      data: TData;
    }
  | {
      requestType?: string;
      type?: string;
      requestId: string;
      success: false;
      error?: string;
      errorMessage?: string;
    };

export interface UpdateParentRsvpPayload {
  eventId: ParentEventId;
  attending: boolean;
  /** Fee the resident pays to attend. Omitted for free events and cancellations. */
  fees?: number;
}

export interface UpdateParentRsvpResponse {
  eventId: ParentEventId;
  attending: boolean;
}

const REQUEST_TIMEOUT_MS = 60_000;
const MOCK_PARENT_DELAY_MS = 1_000;
const mockParentRsvpByEventId = new Map<string, ParentEventDetails>();

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${Date.now()}-${crypto.randomUUID()}`;
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getEventKey = (eventId: ParentEventId) => String(eventId);

const getMockParentEventDetails = (eventId: ParentEventId): ParentEventDetails => {
  const eventKey = getEventKey(eventId);
  const existing = mockParentRsvpByEventId.get(eventKey);
  if (existing) return existing;

  const initialDetails: ParentEventDetails = {
    id: eventId,
    numberAttending: 0,
    totalCapacity: 10,
    isAttending: false,
  };

  mockParentRsvpByEventId.set(eventKey, initialDetails);
  return initialDetails;
};

const waitForMockParent = () =>
  new Promise(resolve => {
    window.setTimeout(resolve, MOCK_PARENT_DELAY_MS);
  });

async function getMockParentResponse<TPayload, TData>(
  requestType: ParentMessageRequest<TPayload>['requestType'],
  payload: TPayload
): Promise<TData> {
  await waitForMockParent();

  if (requestType === 'EVENT_DETAILS') {
    const { eventId } = payload as { eventId: ParentEventId };
    return getMockParentEventDetails(eventId) as TData;
  }

  const { eventId, attending } = payload as { eventId: ParentEventId; attending: boolean };
  const currentDetails = getMockParentEventDetails(eventId);
  const nextDetails: ParentEventDetails = {
    ...currentDetails,
    numberAttending:
      attending && !currentDetails.isAttending
        ? currentDetails.numberAttending + 1
        : !attending && currentDetails.isAttending
          ? Math.max(0, currentDetails.numberAttending - 1)
          : currentDetails.numberAttending,
    isAttending: attending,
  };

  mockParentRsvpByEventId.set(getEventKey(eventId), nextDetails);

  return {
    eventId,
    attending,
  } as TData;
}

function isParentMessageResponse<TData>(
  value: unknown,
  requestType: ParentMessageRequest<unknown>['requestType'],
  requestId: string
): value is ParentMessageResponse<TData> {
  if (!isObject(value)) return false;

  const responseType = value.requestType ?? value.type;
  return (
    responseType === requestType &&
    value.requestId === requestId &&
    typeof value.success === 'boolean'
  );
}

async function requestFromParent<TPayload, TData>(
  requestType: ParentMessageRequest<TPayload>['requestType'],
  payload: TPayload
): Promise<TData> {
  const requestId = createRequestId();
  const message: ParentMessageRequest<TPayload> = {
    requestType,
    requestId,
    payload,
  };

  if (typeof window === 'undefined') {
    throw new Error('postMessage not available outside browser');
  }

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Request timed out'));
    }, REQUEST_TIMEOUT_MS);

    function cleanup() {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timeout);
    }

    function onMessage(event: MessageEvent) {
      if (event.source !== window.parent) return;

      if (!isParentMessageResponse<TData>(event.data, requestType, requestId)) {
        return;
      }

      logger.debug('[Homebody RXP RSVP] postMessage from parent', event.data);
      cleanup();

      if (event.data.success) {
        resolve(event.data.data);
        return;
      }

      reject(new Error(event.data.errorMessage || event.data.error || 'RSVP request failed'));
    }

    window.addEventListener('message', onMessage);
    // Store last request on window for console testing (dev only)
    (window as any).__cobuLastRsvpRequest = message;
    console.log('[Homebody RXP RSVP] postMessage to parent', message);
    window.parent.postMessage(message, '*');
  });
}

export const getParentEventDetails = (eventId: ParentEventId) =>
  requestFromParent<{ eventId: ParentEventId }, ParentEventDetails>('EVENT_DETAILS', {
    eventId,
  });

export const updateParentRsvp = (
  eventId: ParentEventId,
  attending: boolean,
  fees?: number | null
) =>
  requestFromParent<UpdateParentRsvpPayload, UpdateParentRsvpResponse>('UPDATE_RSVP', {
    eventId,
    attending,
    // Only paid attendance carries a fee; cancellations and free events omit it.
    ...(attending && typeof fees === 'number' && fees > 0 ? { fees } : {}),
  });
